// src/utils/monthOverMonthCalculations.ts

import { AsinMonthlyData, MetricConfig } from '@/types/asinMonthlyResultsTypes';
import { 
  MonthIdentifier, 
  MonthComparison, 
  YoYComparisonData,
  YoYCalculationParams,
  YoYCalculationResult
} from '@/types/yearOverYearTypes';

/**
 * Get the current month and 14 previous months for Month Over Month comparison
 */
export const getCurrentAndPreviousMonthsMoM = (): MonthIdentifier[] => {
  const months: MonthIdentifier[] = [];
  const now = new Date();
  
  // Start with current month and go back 14 months (15 total months)
  for (let i = 0; i < 15; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    
    months.push({
      monthName,
      year,
      fullName: `${monthName} ${year}`,
      abbreviation: formatMonthAbbreviationMoM(monthName, year)
    });
  }
  
  return months;
};

/**
 * Format month abbreviation like "Jun. '25"
 */
export const formatMonthAbbreviationMoM = (monthName: string, year: number): string => {
  const monthAbbreviations: { [key: string]: string } = {
    'January': 'Jan.',
    'February': 'Feb.',
    'March': 'Mar.',
    'April': 'Apr.',
    'May': 'May',
    'June': 'Jun.',
    'July': 'Jul.',
    'August': 'Aug.',
    'September': 'Sep.',
    'October': 'Oct.',
    'November': 'Nov.',
    'December': 'Dec.'
  };
  
  const abbreviated = monthAbbreviations[monthName] || monthName;
  const shortYear = year.toString().slice(-2);
  return `${abbreviated} '${shortYear}`;
};

/**
 * Calculate month-over-month change for a specific metric
 */
export const calculateMoMChange = (params: YoYCalculationParams): YoYCalculationResult => {
  const { currentValue, previousValue, metricConfig } = params;
  
  // Handle undefined values
  if (currentValue === undefined && previousValue === undefined) {
    return {
      absoluteChange: undefined,
      percentageChange: undefined,
      formattedDisplay: '-'
    };
  }
  
  if (currentValue === undefined) {
    return {
      absoluteChange: undefined,
      percentageChange: undefined,
      formattedDisplay: 'N/A'
    };
  }
  
  if (previousValue === undefined || previousValue === 0) {
    return {
      absoluteChange: currentValue,
      percentageChange: undefined,
      formattedDisplay: formatMoMDisplay(currentValue, undefined, metricConfig.formatter)
    };
  }
  
  // Calculate changes
  const absoluteChange = currentValue - previousValue;
  const percentageChange = (absoluteChange / previousValue) * 100;
  
  return {
    absoluteChange,
    percentageChange,
    formattedDisplay: formatMoMDisplay(absoluteChange, percentageChange, metricConfig.formatter)
  };
};

/**
 * Format the Month-over-Month display based on metric type
 */
export const formatMoMDisplay = (
  absoluteChange: number | undefined,
  percentageChange: number | undefined,
  formatter: MetricConfig['formatter']
): string => {
  if (absoluteChange === undefined) {
    return '-';
  }
  
  const percentageStr = percentageChange !== undefined
    ? `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(0)}%`
    : '0%';
  
  switch (formatter) {
    case 'currency':
      // Regular currency formatting (whole dollars)
      const currencyStr = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(absoluteChange);
      return `${currencyStr} / ${percentageStr}`;
      
    case 'currencyWithCents':
      // Currency with cents (for PPC Cost Per Click)
      const currencyWithCentsStr = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(absoluteChange);
      return `${currencyWithCentsStr} / ${percentageStr}`;
      
    case 'percentage':
      // For percentage metrics, multiply by 100 and show with one decimal
      const currentPercentageStr = `${(absoluteChange * 100).toFixed(1)}%`;
      return `${currentPercentageStr} / ${percentageStr}`;
      
    case 'wholeNumber':
    default:
      const numberStr = Math.round(absoluteChange).toLocaleString('en-US');
      return `${numberStr} / ${percentageStr}`;
  }
};

/**
 * Get relevant months for Month-over-Month comparison (current + 14 previous)
 */
export const getRelevantMonthsMoM = (): string[] => {
  const months = getCurrentAndPreviousMonthsMoM();
  return months.map(month => month.abbreviation);
};

/**
 * Calculate Month-over-Month comparison for a single ASIN
 */
export interface MonthOverMonthComparison {
  metric: string;
  month: string;
  currentValue: number | null;
  previousValue: number | null;
  absoluteChange: number | null;
  percentageChange: number | null;
  formattedDisplay: string;
}

/**
 * Calculate month-over-month comparisons for an ASIN
 */
export const calculateMonthOverMonthComparison = (
  asinData: AsinMonthlyData,
  relevantMonths: string[]
): MonthOverMonthComparison[] => {
  const comparisons: MonthOverMonthComparison[] = [];
  
  if (!asinData?.monthlyMetrics) {
    return comparisons;
  }

  // Get all 18 metric configurations to match the existing dashboard
  const metricConfigs: MetricConfig[] = [
    { key: 'totalTraffic', label: 'Total Traffic', formatter: 'wholeNumber' },
    { key: 'totalUnits', label: 'Total Units', formatter: 'wholeNumber' },
    { key: 'totalRevenue', label: 'Total Revenue', formatter: 'currency' },
    { key: 'totalGrossProfit', label: 'Total Gross Profit', formatter: 'currency' },
    { key: 'grossProfitPerDay', label: 'Gross Profit Per Day', formatter: 'currency' },
    { key: 'grossProfitMargin', label: 'Gross Profit Margin', formatter: 'percentage' },
    { key: 'overallConversionRate', label: 'Overall Conversion Rate', formatter: 'percentage' },
    { key: 'ppcImpressions', label: 'PPC Impressions', formatter: 'wholeNumber' },
    { key: 'ppcClicks', label: 'PPC Clicks', formatter: 'wholeNumber' },
    { key: 'ppcSales', label: 'PPC Sales', formatter: 'currency' },
    { key: 'ppcSpend', label: 'PPC Spend', formatter: 'currency' },
    { key: 'ppcOrders', label: 'PPC Orders', formatter: 'wholeNumber' },
    { key: 'ppcUnits', label: 'PPC Units', formatter: 'wholeNumber' },
    { key: 'ppcConversionRate', label: 'PPC Conversion Rate', formatter: 'percentage' },
    { key: 'ppcClickThroughRate', label: 'PPC Click Through Rate', formatter: 'percentage' },
    { key: 'ppcCostPerClick', label: 'PPC Cost Per Click', formatter: 'currencyWithCents' },
    { key: 'acos', label: 'ACoS', formatter: 'percentage' },
    { key: 'tacos', label: 'TACoS', formatter: 'percentage' },
  ];

  relevantMonths.forEach((monthAbbrev, index) => {
    // Convert abbreviation back to full month name for data lookup
    const currentMonths = getCurrentAndPreviousMonthsMoM();
    const currentMonth = currentMonths.find(m => m.abbreviation === monthAbbrev);
    
    if (!currentMonth) return;

    // For Month over Month, we compare to the previous month (not previous year)
    const previousMonthIndex = index + 1;
    const previousMonth = previousMonthIndex < currentMonths.length ? currentMonths[previousMonthIndex] : null;

    if (!previousMonth) return; // Skip if no previous month available

    const currentMonthData = asinData.monthlyMetrics?.[currentMonth.fullName];
    const previousMonthData = asinData.monthlyMetrics?.[previousMonth.fullName];

    metricConfigs.forEach(metricConfig => {
      const currentValue = currentMonthData?.[metricConfig.key] ?? null;
      const previousValue = previousMonthData?.[metricConfig.key] ?? null;

      const momResult = calculateMoMChange({
        currentValue: currentValue ?? undefined,
        previousValue: previousValue ?? undefined,
        metricConfig
      });

      comparisons.push({
        metric: metricConfig.label,
        month: monthAbbrev,
        currentValue,
        previousValue,
        absoluteChange: momResult.absoluteChange ?? null,
        percentageChange: momResult.percentageChange ?? null,
        formattedDisplay: momResult.formattedDisplay
      });
    });
  });

  return comparisons;
};

/**
 * Format Month-over-Month value for display
 */
export const formatMonthOverMonthValue = (comparison: MonthOverMonthComparison): string => {
  return comparison.formattedDisplay;
};